import { addCalendarDays, calendarDateRange, type CalendarDate } from "../shared/calendar-date";
import type { WeightEntry } from "./weight-entry";
import { roundTo } from "../shared/validation";

export const FIVE_CALENDAR_DAY_TREND_POLICY_VERSION = "five-calendar-day-trend-v1";

export interface WeightTrendSample {
  readonly date: CalendarDate;
  readonly kilograms: number;
}

export interface WeightTrend {
  readonly policyVersion: typeof FIVE_CALENDAR_DAY_TREND_POLICY_VERSION;
  readonly date: CalendarDate;
  readonly averageKilograms: number;
  readonly sampleCount: number;
  readonly sampleDates: readonly CalendarDate[];
  /** Exact values are retained so a check-in can be reproduced later. */
  readonly samples: readonly WeightTrendSample[];
}

/**
 * A trend for D uses only available weights on D, D-1, ... D-4. Missing dates
 * reduce the denominator; an older measurement is never substituted. Week/check-in
 * boundaries are intentionally absent from this function.
 */
export function fiveCalendarDayWeightTrend(
  entries: readonly WeightEntry[],
  date: CalendarDate,
): WeightTrend | undefined {
  const latestByDate = new Map<CalendarDate, WeightEntry>();
  for (const entry of entries) {
    const existing = latestByDate.get(entry.date);
    if (!existing || entry.updatedAt > existing.updatedAt) latestByDate.set(entry.date, entry);
  }

  const samples = Array.from({ length: 5 }, (_, offset) => addCalendarDays(date, -offset))
    .flatMap((candidate): WeightTrendSample[] => {
      const entry = latestByDate.get(candidate);
      return entry ? [{ date: candidate, kilograms: entry.kilograms }] : [];
    });
  if (samples.length === 0) return undefined;
  const total = samples.reduce((sum, sample) => sum + sample.kilograms, 0);
  return {
    policyVersion: FIVE_CALENDAR_DAY_TREND_POLICY_VERSION,
    date,
    averageKilograms: roundTo(total / samples.length, 3),
    sampleCount: samples.length,
    sampleDates: samples.map((sample) => sample.date),
    samples,
  };
}

export interface WeightChartPoint {
  readonly date: CalendarDate;
  readonly rawKilograms?: number;
  readonly trendKilograms?: number;
  readonly trendSampleCount: number;
}

export function buildWeightChartSeries(
  entries: readonly WeightEntry[],
  start: CalendarDate,
  end: CalendarDate,
): WeightChartPoint[] {
  const byDate = new Map(entries.map((entry) => [entry.date, entry]));
  return calendarDateRange(start, end).map((date) => {
    const trend = fiveCalendarDayWeightTrend(entries, date);
    return {
      date,
      rawKilograms: byDate.get(date)?.kilograms,
      trendKilograms: trend?.averageKilograms,
      trendSampleCount: trend?.sampleCount ?? 0,
    };
  });
}
